/**
 * Planning Calendar V1 (pure). A second PROJECTION of the same Planning tree:
 * no data of its own, no cache of its own.
 *
 * Events are exactly the dated nodes Planning already has:
 *   Project target, Location target, Feature target, Task due.
 * Completed tasks STAY on their due date as Calendar history, marked completed
 * (with their canonical progress/status). They are never overdue, and the
 * Planning action summaries (Overdue / This Week / Upcoming / No Target Date)
 * keep excluding them; those are computed from the tree, not from here.
 * Each task appears once, at its most specific scope (the tree already
 * guarantees that), so nothing is duplicated across levels.
 */
import { PLAN_KIND, dayIndex } from "./planningModel";

export const EVENT_LABEL = Object.freeze({
  [PLAN_KIND.PROJECT]: "Project target",
  [PLAN_KIND.LOCATION]: "Location target",
  [PLAN_KIND.FEATURE]: "Feature target",
  [PLAN_KIND.TASK]: "Task due",
});

const KIND_ORDER = {
  [PLAN_KIND.PROJECT]: 0,
  [PLAN_KIND.LOCATION]: 1,
  [PLAN_KIND.FEATURE]: 2,
  [PLAN_KIND.TASK]: 3,
};

const text = (a, b) => String(a).localeCompare(String(b), "en", { sensitivity: "base" });

/** Flat, deterministic event list from the Planning tree. */
export function buildCalendarEvents(tree = []) {
  const events = [];
  const walk = (node, ancestors, ctx = {}) => {
    const here = {
      projectId: node.kind === PLAN_KIND.PROJECT ? node.id : ctx.projectId,
      locationId: node.kind === PLAN_KIND.LOCATION ? node.id : ctx.locationId,
      featureId: node.kind === PLAN_KIND.FEATURE ? node.id : ctx.featureId,
    };
    if (node.dayIndex != null) {
      const isTask = node.kind === PLAN_KIND.TASK;
      events.push({
        key: `${node.kind}:${node.id}`,
        // Real record identity, so a row can open the exact item it represents.
        id: node.id,
        projectId: here.projectId,
        locationId: here.locationId,
        featureId: here.featureId,
        kind: node.kind,
        label: EVENT_LABEL[node.kind],
        title: node.name,
        context: node.kind === PLAN_KIND.PROJECT ? "" : ancestors.join(" › "),
        path: [...ancestors, node.name].join(" › "),
        date: node.date,
        dayIndex: node.dayIndex,
        overdue: !node.completed && Boolean(node.overdue),
        // Canonical task state for the pill (tasks only; read, never inferred).
        completed: isTask ? Boolean(node.completed) : false,
        progress: isTask ? (node.progress ?? null) : null,
        // Scheduling facts for the selected-day card (tasks only; read from the
        // tree, never inferred). The event's own date is the Due date.
        startDate: isTask ? node.startDate || null : null,
        durationMinutes: isTask ? node.durationMinutes ?? null : null,
        expectedDate: isTask ? node.expectedDate || null : null,
        spanStatus: isTask ? node.spanStatus || null : null,
        expectedAfterDue: isTask ? Boolean(node.expectedAfterDue) : false,
      });
    }
    node.children.forEach((child) => walk(child, [...ancestors, node.name], here));
  };
  tree.forEach((project) => walk(project, []));
  return events;
}

/** Project > Location > Feature > Task, then project/path/title alphabetically. */
export function compareEvents(a, b) {
  return (
    KIND_ORDER[a.kind] - KIND_ORDER[b.kind] ||
    text(a.path.split(" › ")[0], b.path.split(" › ")[0]) ||
    text(a.path, b.path) ||
    text(a.key, b.key)
  );
}

export function indexEventsByDay(events = []) {
  const map = new Map();
  events.forEach((event) => {
    const list = map.get(event.dayIndex) || [];
    list.push(event);
    map.set(event.dayIndex, list);
  });
  map.forEach((list) => list.sort(compareEvents));
  return map;
}

/** First-of-month day index for any day index. */
export function monthStartIndex(index) {
  const d = new Date(index * 86400000);
  return Math.round(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1) / 86400000);
}

export function addMonths(monthStart, delta) {
  const d = new Date(monthStart * 86400000);
  return Math.round(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + delta, 1) / 86400000);
}

/** Sunday-first weeks covering the month (spillover days included). */
export function monthGrid(monthStart) {
  const d = new Date(monthStart * 86400000);
  const first = monthStart;
  const lastOfMonth =
    Math.round(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1) / 86400000) - 1;
  const gridStart = first - d.getUTCDay();
  const gridEnd = lastOfMonth + (6 - new Date(lastOfMonth * 86400000).getUTCDay());
  const weeks = [];
  for (let start = gridStart; start <= gridEnd; start += 7) {
    weeks.push(Array.from({ length: 7 }, (_, i) => start + i));
  }
  return weeks;
}

export function dayInMonth(index, monthStart) {
  return monthStart === monthStartIndex(index);
}

export function eventsForDay(byDay, index) {
  return byDay.get(index) || [];
}

export { dayIndex };
